/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { Navigate } from 'react-router-dom';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';
import {
  CLI_LIKE_CLIENTS,
  LOGIN_REQUIRED_CLIENTS,
} from '../consts/authentication';
import { LOCAL_STORAGE_KEYS } from '../consts/localStorage';
import { ORG_PERMISSIONS } from '../consts/plans';
import { ConfigClient } from '../network/httpClients/ConfigClient/ConfigClient';
import { useOIDCUserInfoQuery } from '../network/queries';
import { QUERY_PARAMS } from '../routes/consts';
import { LOGIN_SUCCESS } from '../services/analytics/events';
import { gtagService } from '../services/analytics/gtag';
import { mixpanelService } from '../services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from '../services/mixpanel/consts';
import { LoginMethod } from '../services/mixpanel/types';
import { Token } from '../types/token';
import { oidcSignout } from '../utils/authentication';
import { isAirgappedMode } from '../utils/env';
import { clearTokens, fetchToken, getAccessToken } from '../utils/token';
import {
  getQueryParam,
  isLoginPage,
  isSuccessSigninExternalClientPage,
  shouldPersistAuthData,
} from '../utils/url';
import { PERMITTED_ROUTES } from './consts';
import permissions from './permissions.json';
import { AuthContextType, AuthProviderProps, UserData } from './types';

// auth context
export const AuthContext = createContext<AuthContextType>({
  isInitialized: false,
  user: null,
  isOrganizationOwner: false,
  signout: async () => {},
  update: () => undefined,
  permission: null,
});

/**
 * Dedicated-org CLI logins must complete at the user's ST (its own keystore), but the CLI
 * success page makes no verify_user_token-gated call, so nothing triggers the existing
 * MT→ST 409 tenant-redirect. This fires one such call to provoke it, reusing the shared
 * 409 interceptor (appendCliRedirectParams + token→cookie migration + navigate to ST).
 *
 * CLI logins don't persist tokens (shouldPersistAuthData=false), so the probe would have no
 * Bearer and the interceptor's cookie-migration nothing to migrate. So we persist the
 * just-fetched token first; capture the user_code (the interceptor carries it to the ST);
 * and — only when NO redirect happens (individual user / non-dedicated org) — clear the
 * tokens back out so a non-ST CLI login doesn't leave creds lingering in storage. A
 * dedicated-org login 409s: the interceptor navigates away, so this promise never resolves.
 */
const maybeRedirectCliLoginToSt = async (
  token: Token | undefined,
): Promise<void> => {
  if (!token?.access_token || !token?.refresh_token) {
    return;
  }

  // Persist directly: storeTokens() no-ops for CLI success pages (shouldPersistAuthData).
  // Storage can throw (blocked/disabled/quota); if we can't persist, the probe would go out
  // without a Bearer (401, not the 409 we need) — so bail rather than fire a broken probe.
  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token.access_token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, token.id_token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, token.refresh_token);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRY,
      (Date.now() + token.expires_in * 1000).toString(),
    );
  } catch {
    return; // storage unavailable — fall through to manual DeviceCodeVerify on the MT
  }

  try {
    // Any verify_user_token-gated endpoint works; settings is the lightest. A dedicated-org
    // user 409s → interceptor redirects (navigates away, we never reach the finally below).
    await ConfigClient.getSettings();
  } catch {
    // 409 → RedirectInProgressError (already navigating); any other error → not a dedicated
    // org. Either way the finally clears the creds we persisted.
  } finally {
    // Always restore the shouldPersistAuthData=false intent: on the no-redirect path (and if
    // the probe is interrupted) don't leave a non-ST CLI login's creds lingering in storage.
    // On the redirect path this is unreachable (navigation already tore the page down), and
    // the interceptor already migrated the tokens to the shared cookie beforehand.
    clearTokens();
  }
};

export function AuthProvider(props: AuthProviderProps) {
  const path = window.location.pathname;
  const cachedUserString = localStorage.getItem('user');
  const cachedUser = cachedUserString ? JSON.parse(cachedUserString) : null;

  const [user, setUser] = useState<UserData | null | undefined>(cachedUser);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldFetchOIDCUser, setShouldFetchOIDCUser] = useState(false);
  const [shouldFireLoginSuccessEvent, setShouldFireLoginSuccessEvent] =
    useState(false);
  const [shouldFireSignupEvent, setShouldFireSignupEvent] = useState(false);
  const capturedProviderRef = useRef<string | undefined>(undefined);

  // Use the OIDC user info query
  const {
    data: oidcUserInfo,
    isSuccess: isOIDCUserInfoSuccess,
    isError: isOIDCUserInfoError,
  } = useOIDCUserInfoQuery({
    enabled: shouldFetchOIDCUser,
    // A 403 "Not authenticated" (no session) won't recover on retry; don't delay
    // the /signin redirect with pointless retries.
    retry: false,
  });

  // Effect to handle OIDC user info when it's fetched
  useEffect(() => {
    if (isOIDCUserInfoSuccess && oidcUserInfo) {
      const userData = {
        ...oidcUserInfo,
        ...oidcUserInfo.metadata,
        // PLT-4142: metadata.tierLevel defaults to ENTERPRISE when the tier
        // claim is absent; the top-level tierLevel/tierEndDate (from the
        // tier_level claim) are authoritative. Re-assert them after the metadata
        // spread so the buggy default can't overwrite them, mirroring
        // transformUserInfoToUser's top-level-first precedence. Guarded on
        // presence, so github-only / airgapped providers that carry tier only
        // via metadata are unchanged.
        ...(oidcUserInfo.tierLevel !== undefined && {
          tierLevel: oidcUserInfo.tierLevel,
        }),
        ...(oidcUserInfo.tierEndDate !== undefined && {
          tierEndDate: oidcUserInfo.tierEndDate,
        }),
      } as UserData;

      if (userData.hasOwnProperty('metadata')) {
        delete userData.metadata;
      }

      setUser(userData);

      if (shouldPersistAuthData()) {
        localStorage.setItem('user', JSON.stringify(userData));
      }

      if (shouldFireLoginSuccessEvent) {
        mixpanelService.track(MIXPANEL_EVENT_NAMES.SYSTEM, {
          operation: MIXPANEL_OPERATIONS.PORTAL_LOGIN_SUCCESS,
          interaction_details: {
            login_method:
              (getQueryParam(QUERY_PARAMS.PROVIDER) as LoginMethod) ||
              undefined,
          },
        });

        gtagService.trackEvent(LOGIN_SUCCESS, {
          user_id: userData?.id,
          method: capturedProviderRef.current || 'unknown',
        });

        setShouldFireLoginSuccessEvent(false);
      }

      if (
        shouldFireSignupEvent &&
        !sessionStorage.getItem(SESSION_STORAGE_KEYS.SIGNUP_EVENT_FIRED)
      ) {
        gtagService.trackEvent('signup_success', {
          user_id: userData?.id,
          method: capturedProviderRef.current || 'unknown',
        });

        sessionStorage.setItem(SESSION_STORAGE_KEYS.SIGNUP_EVENT_FIRED, 'true');
        setShouldFireSignupEvent(false);
      }

      setIsInitialized(true);
    }
  }, [
    isOIDCUserInfoSuccess,
    oidcUserInfo,
    shouldFireLoginSuccessEvent,
    shouldFireSignupEvent,
  ]);

  // The auth probe failed (e.g. 403 "Not authenticated" with no session, or a
  // network error). Not a hang state: finish initialization so the router renders
  // and sends unauthenticated users to /signin (the "*" fallback in Routes.tsx).
  // Without this, isInitialized never flips on a probe error and the app is stuck
  // on the Loader forever.
  useEffect(() => {
    if (isOIDCUserInfoError) {
      setIsInitialized(true);
    }
  }, [isOIDCUserInfoError]);

  // This effect handles the OIDC auth protocol login flow
  useEffect(() => {
    if (isAirgappedMode()) {
      setIsInitialized(true);

      return;
    }

    // Add this check to prevent execution on login pages. Instead, if the user is already authenticated, we can set the isInitialized state to true and let the login protected route handle the redirect back to the app.
    if (isLoginPage() && !isSuccessSigninExternalClientPage()) {
      setIsInitialized(true);

      return;
    }

    if (path.includes('/error')) {
      setIsInitialized(true);

      return;
    }

    const clientQueryParam =
      getQueryParam(QUERY_PARAMS.INIT_CLIENT) || 'portal';

    const storedAccessToken = getAccessToken();

    if (
      storedAccessToken &&
      !LOGIN_REQUIRED_CLIENTS.includes(clientQueryParam)
    ) {
      setShouldFetchOIDCUser(true);

      return;
    }

    capturedProviderRef.current =
      getQueryParam(QUERY_PARAMS.PROVIDER) || undefined;

    fetchToken()
      .then(async (token) => {
        if (token?.signup) {
          setShouldFireSignupEvent(true);
        } else {
          setShouldFireLoginSuccessEvent(true);
        }

        // For external clients on the success page, don't fetch user info - just set initialized
        if (
          isSuccessSigninExternalClientPage() &&
          LOGIN_REQUIRED_CLIENTS.includes(clientQueryParam)
        ) {
          // Dedicated-org CLI logins must complete at the user's ST (its own keystore).
          // The CLI success page makes no verify_user_token-gated call, so nothing triggers
          // the existing MT→ST 409 tenant-redirect. Fire one here: on a dedicated org it
          // 409s and the shared interceptor redirects to the ST (carrying the device code);
          // otherwise it's a no-op and the login completes on the MT via DeviceCodeVerify.
          //
          // AWAIT it (don't fire-and-forget): a dedicated-org user's probe 409s and the
          // interceptor navigates to the ST, so this promise never resolves and we stay on the
          // Loader — the MT device-code form is NEVER shown, so the user can't accidentally
          // type their code into the wrong (MT) deployment during the redirect window. Only an
          // individual/non-dedicated CLI login resolves here, after which we render the MT form.
          if (CLI_LIKE_CLIENTS.includes(clientQueryParam)) {
            await maybeRedirectCliLoginToSt(token);
          }

          setIsInitialized(true);
        } else {
          setShouldFetchOIDCUser(true);
        }

        if (shouldPersistAuthData()) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_V2, 'true');
        }
      })
      .catch((error) => {
        console.error('Failed to fetch token on app mount: ', error);
        setIsInitialized(true);
      });
  }, []);

  async function signout(): Promise<void> {
    const signoutFunc = oidcSignout;

    setUser(undefined);
    signoutFunc();
  }

  function update(data: Partial<UserData>): UserData | undefined {
    const userString = localStorage.getItem('user');

    if (userString) {
      let user = JSON.parse(userString) as UserData;

      for (let key in data) {
        if (Array.isArray(data[key])) {
          user[key] = data[key];
        } else if (typeof data[key] === 'object' && data[key] !== null) {
          const dataValue = data[key] as Record<string, unknown>;
          const userValue = user[key] as Record<string, unknown>;

          for (let innerKey in dataValue) {
            if (typeof userValue === 'object' && userValue !== null) {
              userValue[innerKey] = dataValue[innerKey];
            }
          }
        } else {
          user[key] = data[key];
        }
      }

      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return user;
    }

    return undefined;
  }

  return (
    <AuthContext.Provider
      value={{
        isInitialized,
        user: user as UserData,
        isOrganizationOwner:
          (user as UserData)?.organization_permission === ORG_PERMISSIONS.OWNER,
        signout,
        update,
        permission:
          permissions[
            (user as UserData)?.permission as keyof typeof permissions
          ],
      }}
      {...props}
    />
  );
}

interface PrivateRouteProps {
  children: ReactNode;
  permission: string;
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props: PrivateRouteProps) {
  // check user exists
  const userString = localStorage.getItem('user');
  const user = userString ? (JSON.parse(userString) as UserData) : null;
  const path = window.location.pathname;

  if (user?.token) {
    const userPermission = user.permission as keyof typeof permissions;

    if (
      permissions[userPermission]?.[
        props.permission as keyof (typeof permissions)[typeof userPermission]
      ]
    ) {
      if (user.verified) {
        // user has no plan
        if (
          !user.plan &&
          path !== '/account/profile' &&
          path !== '/signup/plan'
        )
          return <Navigate to="/signup/plan" />;

        // user has no subscription
        if (
          user.subscription !== 'active' &&
          user.subscription !== 'trialing' &&
          user.permission !== 'master' &&
          !PERMITTED_ROUTES.includes(path)
        )
          return <Navigate to="/account/billing" />;

        // hide verified view if user is verified
        if (path === '/signup/verify') return <Navigate to="/dashboard" />;
      } else {
        // user is not verified
        // if (path !== '/account/profile' && path !== '/signup/verify')
        //   return <Navigate to='/signup/verify' />;
      }

      // user is good
      return props.children;
    }
  }

  // user is not authenticated
  return <Navigate to="/signin" />;
}
